
import { CooksyLogo } from "@/components/CooksyLogo";
import { AdminNav } from "./AdminNav";
import { usePlatform } from "@/contexts/PlatformContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { cn } from "@/lib/utils";

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  showBackButton?: boolean;
}

export const AdminLayout = ({ children, title, showBackButton }: AdminLayoutProps) => {
  const { isDesktop, isMobileWeb } = usePlatform();
  const navigate = useNavigate();
  
  // Ensure proper scrolling on mobile admin pages
  useEffect(() => {
    if (!isDesktop) {
      // Enable scrolling on mobile admin pages with enhanced settings
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
      document.body.style.position = "relative";
      
      // Safari-specific fixes for admin pages
      document.body.style.height = "auto";
      document.body.style.minHeight = "100vh";
      document.documentElement.style.height = "auto";
      document.documentElement.style.minHeight = "100vh";
      
      // Enable momentum scrolling for iOS using type assertion
      (document.body.style as any).webkitOverflowScrolling = "touch";
      (document.body.style as any).overflowScrolling = "touch";
    }
    
    return () => {
      // Clean up on unmount
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.height = "";
      document.body.style.minHeight = "";
      document.documentElement.style.height = "";
      document.documentElement.style.minHeight = "";
      document.body.style.position = "";
      (document.body.style as any).webkitOverflowScrolling = "";
      (document.body.style as any).overflowScrolling = "";
    };
  }, [isDesktop]);
  
  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      {!isDesktop && (
        <header className="bg-white shadow-sm py-4 px-4 mb-4 fixed top-0 left-0 right-0 z-30">
          <div className="container mx-auto px-0 flex justify-between items-center">
            <div className="flex items-center gap-2">
              {showBackButton && (
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <CooksyLogo />
            </div>
            <h1 className="text-xl font-semibold text-black">{title}</h1>
            <div className="w-8"></div> {/* Empty div for balanced flex */}
          </div>
        </header>
      )}
      
      <div className="flex flex-col md:flex-row min-h-screen">
        {isDesktop && <div className="w-64"><AdminNav /></div>}
        
        <main className={cn(
          "flex-1",
          // Mobile: Enable scrolling with proper padding and height
          !isDesktop ? "pt-20 pb-24 px-4 overflow-y-auto min-h-screen relative" : "",
          // Desktop: Standard layout
          isDesktop ? "overflow-y-auto" : ""
        )}>
          {isDesktop && (
            <div className="py-4 px-6 border-b mb-4">
              <h1 className="text-xl font-semibold text-black">{title}</h1>
            </div>
          )}
          <div className={cn(
            isDesktop ? "px-6" : "",
            // Ensure content has proper spacing on mobile with touch scrolling
            !isDesktop ? "pb-safe touch-pan-y" : ""
          )}>
            {children}
          </div>
        </main>
      </div>
      
      {/* AdminNav for mobile is rendered as a floating button with lower z-index */}
      {!isDesktop && <AdminNav />}
    </div>
  );
};
